import { Link } from "react-router-dom";
import { MapPin, Mail } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import SocialLinks from "@/components/SocialLinks";
import { WhatsAppIcon, WHATSAPP_NUMBERS } from "@/components/WhatsAppNumbers";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/jsnv4v6a_1000368650.jpg";

export default function Footer() {
  const { t } = useLang();

  return (
    <footer className="bg-[#0a0a0a] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="KMSN" className="h-12 w-12 object-contain rounded" />
              <div>
                <h3 className="font-heading text-white text-xl">KMSN</h3>
                <p className="text-[#C8A951] text-xs tracking-[0.2em] uppercase font-body">{t("slogan").substring(0, 25)}</p>
              </div>
            </div>
            <p className="text-[#A1A1AA] text-sm font-body leading-relaxed mt-2">{t("footerDesc")}</p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.3em] text-[#C8A951] mb-6 font-body">{t("navigation")}</h4>
            <div className="flex flex-col gap-3">
              <Link to="/" className="text-[#A1A1AA] hover:text-white transition-colors text-sm font-body" data-testid="footer-home-link">{t("home")}</Link>
              <Link to="/construction" className="text-[#A1A1AA] hover:text-white transition-colors text-sm font-body" data-testid="footer-construction-link">KMSN {t("construction")}</Link>
              <Link to="/travaux" className="text-[#A1A1AA] hover:text-white transition-colors text-sm font-body" data-testid="footer-travaux-link">KMSN {t("travaux")}</Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.3em] text-[#C8A951] mb-6 font-body">{t("contactTitle")}</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-[#C8A951] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-[#A1A1AA] text-sm font-body">Marrakech - Ouarzazate - Tinghir - Errachidia</span>
              </div>
              {WHATSAPP_NUMBERS.map((item) => (
                <a key={item.number} href={`https://wa.me/${item.number.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group" data-testid={`footer-whatsapp-${item.number}`}>
                  <WhatsAppIcon size={16} />
                  <span className="text-[#A1A1AA] text-sm font-body group-hover:text-[#25D366] transition-colors">{item.flag} {item.display}</span>
                </a>
              ))}
              <a href="mailto:kmsntravaux@gmail.com" className="flex items-center gap-3 group" data-testid="footer-email-link">
                <Mail size={16} className="text-[#C8A951] flex-shrink-0" strokeWidth={1.5} />
                <span className="text-[#A1A1AA] text-sm font-body group-hover:text-white transition-colors">kmsntravaux@gmail.com</span>
              </a>
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.3em] text-[#C8A951] mb-6 font-body">{t("followUs")}</h4>
            <SocialLinks direction="col" />
          </div>
        </div>

        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#A1A1AA]/60 text-xs font-body">&copy; {new Date().getFullYear()} KMSN. {t("allRights")}</p>
          <SocialLinks direction="row" compact />
        </div>
      </div>
    </footer>
  );
}
